import { customElement, bindable, inject, bindingMode, TaskQueue, Loader } from 'aurelia-framework';

/* Due to a current limitation in Aurelia's templating, it's not possible to declare multiple
methods in an event handler with separate binding behaviors. We work around this by duplicating
the event name by adding these lines to our ViewModel. */
import { SyntaxInterpreter } from 'aurelia-templating-binding';
import { CarrierDocument } from '../../models/carrier-document';
import { BusyService } from '../../services/busy-service';
(<any>SyntaxInterpreter.prototype).trigger2 = SyntaxInterpreter.prototype.trigger;

@customElement('pdf-document')
@inject(TaskQueue, Loader, BusyService)
export class PdfDocument {

    @bindable() documentId: string;
    @bindable({ defaultValue: 1, defaultBindingMode: bindingMode.twoWay }) page: number;
    @bindable({ defaultValue: 1, defaultBindingMode: bindingMode.twoWay }) lastPage: number;
    @bindable({ defaultValue: 1, defaultBindingMode: bindingMode.twoWay }) scale: number;
    @bindable({ defaultBindingMode: bindingMode.oneWay }) viewerHeight: string;

    container: any;
    pdfjsLib: any;
    documentPending: any;
    resolveDocumentPending: any;
    currentPage: any;
    pages: any[];
    fingerprint: string;
    worker: any;
    taskQueue: any;
    constructor(taskQueue, loader, private busyService) {

        this.pdfjsLib = window["pdfjsLib"];
        this.pdfjsLib.workerSrc = '//scripts/pdfjs/pdf.worker.js';

        this.taskQueue = taskQueue;
        this.worker = new this.pdfjsLib.PDFWorker();

        this.fingerprint = generateUniqueDomId();
        this.pages = [];

        this.currentPage = null;
        this.resolveDocumentPending;
    }

    attached() {
    }

    detached() {
        if (this.documentPending) {
            return this.documentPending
                .then((pdf) => {
                    if (pdf) {
                        pdf.destroy();
                    }
                    this.worker.destroy();
                })
                .catch(() => {
                    debugger;
                    this.worker.destroy();
                });
        }
    }

    getUrl(id) {
        if (id) {
            return "/api/Document/DownloadDocument?id=" + id;
        }
    }


    documentIdChanged(newValue: string, oldValue: string) {
        if (!newValue)
            return;

        var promise = this.documentPending || Promise.resolve();
        this.documentPending = new Promise((resolve, reject) => {
            this.resolveDocumentPending = resolve.bind(this);
        });
        return promise
            .then((pdf) => {
                if (pdf) {
                    pdf.destroy();
                }
                let url = this.getUrl(newValue);
                var alwaysSuccessfulPromise = new Promise(resolve => {
                    if (url) {
                        this.busyService.on();
                        var task = this.pdfjsLib.getDocument({ url: url, worker: this.worker });

                        task.promise
                            .then(result => {
                                resolve(result);
                            }, error => {
                                resolve();
                                console.error("Error downloading document version: " + url);
                            })
                            .finally(() => {
                                this.busyService.off();
                            });
                    } else {
                        resolve();
                    }
                });
                return alwaysSuccessfulPromise;
            })
            .then((pdf) => {
                if (pdf) {

                    this.lastPage = pdf.numPages;

                    pdf.cleanupAfterRender = true;
                    for (var i = 0; i < pdf.numPages; i++) {
                        this.pages[i] = pdf.getPage(Number(i + 1))
                            .then((page) => {
                                let viewport = page.getViewport(this.scale);
                                let element: any = document.getElementById(`${this.fingerprint}-page${page.pageNumber}`);

                                this.taskQueue.queueMicroTask(() => {
                                    element.height = viewport.height;
                                    element.width = viewport.width;
                                });

                                return {
                                    element: element,
                                    page: page,
                                    rendered: false,
                                    clean: false
                                };
                            })
                    }

                    this.pages.forEach((page) => {
                        page.then((renderObject) => {
                            if (checkIfElementVisible(this.container, renderObject.element)) {
                                if (renderObject.rendered) return;
                                render(page, this.scale);
                            }
                        });
                    });

                }
                this.resolveDocumentPending(pdf);
            });
    }

    pageChanged(newValue, oldValue) {

        if (newValue != 0 && !newValue)
            return;

        if (newValue === oldValue || isNaN(Number(newValue)) || Number(newValue) > this.lastPage || Number(newValue) < 0) {
            this.page = oldValue;
            return;
        }

        if (Math.abs(newValue - oldValue) <= 0) return;

        this.pages[newValue - 1]
            .then((renderObject) => {
                this.container.scrollTop = renderObject.element.offsetTop;
                render(this.pages[newValue - 1], this.scale);
            });
    }

    scaleChanged(newValue, oldValue) {
        if (newValue === oldValue || isNaN(Number(newValue))) return;

        Promise.all(this.pages)
            .then((values) => {
                values.forEach((renderObject) => {
                    if (!renderObject) return;

                    var viewport = renderObject.page.getViewport(newValue);

                    renderObject.rendered = false;

                    this.taskQueue.queueMicroTask(() => {
                        renderObject.element.height = viewport.height;
                        renderObject.element.width = viewport.width;

                        if (renderObject.page.pageNumber === this.page) {
                            this.container.scrollTop = renderObject.element.offsetTop;
                        }
                    });
                });

                return values;
            })
            .then((values) => {
                this.pages.forEach((page) => {
                    page.then((renderObject) => {
                        this.taskQueue.queueMicroTask(() => {
                            if (checkIfElementVisible(this.container, renderObject.element)) {
                                render(page, this.scale);
                            }
                        });
                    });
                });
            });
    }

    pageHandler() {
        this.pages.forEach((page) => {
            page.then((renderObject) => {
                if ((this.container.scrollTop + this.container.clientHeight) >= renderObject.element.offsetTop
                    && (this.container.scrollTop <= renderObject.element.offsetTop)) {
                    this.page = renderObject.page.pageNumber;
                }
            });
        });
    }

    renderHandler() {
        Promise.all(this.pages)
            .then((values) => {
                values.forEach((renderObject) => {
                    if (!renderObject) return;

                    if (!checkIfElementVisible(this.container, renderObject.element)) {
                        if (renderObject.rendered && renderObject.clean) {
                            renderObject.page.cleanup();
                            renderObject.clean = true;
                        }

                        return;
                    }

                    this.taskQueue.queueMicroTask(() => {
                        if (renderObject.rendered) return;
                        render(renderObject, this.scale);
                    });
                });
            });
    }
}

var generateUniqueDomId = function () {
    var S4 = function () {
        return (((1 + Math.random()) * 0x10000) | 0)
            .toString(16)
            .substring(1);
    };

    return `_${S4()}${S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`;
}

var checkIfElementVisible = function (container, element) {
    var containerBounds = {
        top: container.scrollTop,
        bottom: container.scrollTop + container.clientHeight
    };

    var elementBounds = {
        top: element.offsetTop,
        bottom: element.offsetTop + element.clientHeight
    };

    return (!((elementBounds.bottom < containerBounds.top && elementBounds.top < containerBounds.top)
        || (elementBounds.top > containerBounds.bottom && elementBounds.bottom > containerBounds.bottom)));
}

var render = function (renderPromise, scale) {
    return Promise.resolve(renderPromise)
        .then((renderObject) => {
            if (renderObject.rendered) return Promise.resolve(renderObject);
            renderObject.rendered = true;

            var viewport = renderObject.page.getViewport(scale);
            var context = renderObject.element.getContext('2d');

            return renderObject.page.render({
                canvasContext: context,
                viewport: viewport
            })
                .promise.then(() => {
                    return renderObject;
                });
        });
};